import {isURLMatching, isMethodMatching, isRequestBodyMatching} from 'shared/utils/networkRequestMatching'
import getResponseTimeDifferenceInMs from './getResponseTimeDifferenceInMs'

/**
 * Adds a `responseBody` property to the passed `WebRequest` request
 * and possibly a `requestBody` as well - if available and recognizable.
 *
 * Network data is recorded in `network.js` and `instrumentNetwork.js`.
 * The former records data with the extension `WebRequest` API,
 * the latter from monkey-patched `fetch` and `XMLHttpRequest` methods.
 * The reason to use two data sources:
 * - `WebRequest` API is reliable and allows to record non-XHR requests.
 * - The monkey patched methods allow to record request responses.
 *   (Firefox has this feature, but Chrome doesn't:
 *   https://bugs.chromium.org/p/chromium/issues/detail?id=487422&q=requestBody&can=2)
 *
 * Unfortunately there's no common request identifier, which is why this function compares
 * request method, URL, request body, and response time to recognize
 * the right `fetch` / `XMLHttpRequest` for the passed `WebRequest` API request.
 * Some attributes need to be normalized / rounded for matching:
 * - The request URL can come without protocol &/ origin for the monkey-patched methods.
 * - The request body can have different types &/ be encoded differently
 *   between `WebRequest` API, `fetch`, `XMLHttpRequest`.
 * - The request's response timestamp differs because the `WebRequest` API and the
 *   monkey-patched methods are called at slightly different times. The time
 *   difference between the data sources grows, the larger the response body is,
 *   and the slower the computer is. This attribute is most tricky, because
 *   being too forgiving can mean showing incorrect data to the user, while
 *   being too strict means not showing response bodies for requests even though
 *   the data is recorded.
 * This function is called on the client whenever opening a network request's details.
 * While it's not elegant to repeatedly re-compute the `responseBody` on the client,
 * the upside is that our sessions contain the recorded raw data from the different
 * sources, and we can ship changes to this algorithm,
 * which then apply to all existing sessions, in a non-destructive way.
 *
 * Good sessions in dev Hasura to test this with:
 * - Bj033wS-_i6DyhCAziw36
 * - 7GCZLnUtmVlGveRha4R1h
 * - tscmTORzG5LWbyggYB38k
 * - Dsu4MF3AZb1xeNX-iT6NA
 */
function enrichRequest(request, requestsFromExtensionAPI, requestsFromInstrumentation) {
	// It's not possible to find a monkey patched request with response
	// without response end timestamp, or when the request is not an XHR request.
	if (!request.endedAt) return request
	if (request.type !== 'fetch' && request.type !== 'xmlhttprequest') return request

	// Filter the list of all `fetch` and `XMLHttpRequest` requests down to the ones with
	// the same URL and request method as the `WebRequest` API request for which a match should be found.
	let requestMatchesFromInstrumentation = []
	requestsFromInstrumentation.forEach(requestToMatch => {
		if (
			isURLMatching(request.url, requestToMatch.url) &&
			isMethodMatching(request.method, requestToMatch.method) &&
			isRequestBodyMatching({
				requestBodyFromInstrumentation: requestToMatch.requestBody,
				requestBodyFromExtensionAPI: request.requestBody,
			})
		) {
			// Compute the time difference of the response between the `WebRequest` API request which should be matched
			// and this matching `fetch` / `XMLHttpRequest`.
			// NOTE: For  `fetch` / `XMLHttpRequest`, the `createdAt` attribute is the timestamp at which the response came in,
			//       which equals to the `WebRequest` API request's `endedAt` attribute.
			requestToMatch.responseTimeDifferenceInMs = getResponseTimeDifferenceInMs(
				request.endedAt,
				requestToMatch.createdAt || requestToMatch.endedAt
			)
			requestMatchesFromInstrumentation.push(requestToMatch)
		}
	})

	// Exit early when no request has been recorded from `fetch` or `XMLHttpRequest` which matches the
	// `WebRequest` API's request's method, URL, and request body.
	if (requestMatchesFromInstrumentation.length < 1) return request

	// Filter the list of all `WebRequest` API requests to all with the same URL, request method,
	// and request body as the request for which a `fetch` / `XMLHttpRequest` match should be found.
	// These requests from the `WebRequest` API compete for the same `fetch` / `XMLHttpRequest` requests for a match.
	// All requests from `WebRequest` API need to be compared to all similar `fetch` / `XMLHttpRequest` to be sure that
	// the correct matching combination is found, and not just the first one which somewhat fits.
	const requestsCompetingFromExtensionAPI = []
	requestsFromExtensionAPI?.forEach(requestFromExtensionAPI => {
		if (requestFromExtensionAPI.url !== request.url) return
		if (requestFromExtensionAPI.method?.toUpperCase() !== request.method?.toUpperCase()) return
		// Also ignore requests without `endedAt` attribute, as those can't be compared by time.
		if (!requestFromExtensionAPI.endedAt) return

		requestsCompetingFromExtensionAPI.push({...requestFromExtensionAPI})
	})

	// Order the matching `fetch` / `XMLHttpRequest` by increasing response time difference.
	requestMatchesFromInstrumentation.sort(
		(request1, request2) =>
			Math.abs(request1.responseTimeDifferenceInMs) - Math.abs(request2.responseTimeDifferenceInMs)
	)

	// Find the matching `fetch` / `XMLHttpRequest` with the smallest response time difference for the `WebRequest` API
	// request, where no other `WebRequest` API request has a smaller response time difference.
	const requestMatchFromInstrumentation = requestMatchesFromInstrumentation.find(
		matchingRequestFromInstrumentation => {
			let bestMatchingRequestFromExtensionAPIForRequestFromInstrumentation

			for (const competingRequestFromExtensionAPI of requestsCompetingFromExtensionAPI) {
				if (
					!isRequestBodyMatching({
						requestBodyFromExtensionAPI: competingRequestFromExtensionAPI.requestBody,
						requestBodyFromInstrumentation: matchingRequestFromInstrumentation.requestBody,
					})
				) {
					continue
				}

				const responseTimeDifferenceInMs =
					new Date(matchingRequestFromInstrumentation.createdAt) -
					new Date(competingRequestFromExtensionAPI.endedAt)

				if (
					!bestMatchingRequestFromExtensionAPIForRequestFromInstrumentation ||
					Math.abs(
						bestMatchingRequestFromExtensionAPIForRequestFromInstrumentation.responseTimeDifferenceInMs
					) > Math.abs(responseTimeDifferenceInMs)
				) {
					bestMatchingRequestFromExtensionAPIForRequestFromInstrumentation = {
						requestFromExtensionAPI: competingRequestFromExtensionAPI,
						responseTimeDifferenceInMs,
					}
				}
			}

			if (
				bestMatchingRequestFromExtensionAPIForRequestFromInstrumentation?.requestFromExtensionAPI.id ===
				request.id
			) {
				return true
			}
			return false
		}
	)

	// No matching `fetch` / `XMLHttpRequest` found where no other `WebRequest` API request
	// responded in closer time proximation.
	if (!requestMatchFromInstrumentation) return request

	// If the passed `WebRequest` API request has a matching `fetch` / `XMLHttpRequest` request,
	// set the request and response body properties, and return it -
	// but only if the response time difference is within a certain threshold. Not setting a threshold or a too high one
	// means that e.g. requests are mismatched when a page is rapidly reloaded, or an API polled.
	// Setting it too narrow on the other hand prevents the request from being enriched correctly.
	// Usually the monkey-patched `fetch` / `XMLHttpResponses` come in later than the `WebRequest` API -
	// only in rare cases, it goes ever-so-slightly in the other direction.
	// The thresholds identified are magic numbers, because the delay varies drastically depending on runtime factors
	// like the request's size, and the performance of the client computer which recorded the session.
	// The threshold is looser when the two data sources recorded the same amount of matching requests,
	// as this reduces the risk of mis-attributing requests.
	const requestCountsMatch = requestsCompetingFromExtensionAPI.length === requestMatchesFromInstrumentation.length
	const maximumRequestResponseDelayOfInstrumentationInMs = requestCountsMatch ? 750 : 175
	if (
		requestMatchFromInstrumentation.responseTimeDifferenceInMs < maximumRequestResponseDelayOfInstrumentationInMs &&
		requestMatchFromInstrumentation.responseTimeDifferenceInMs > -5
	) {
		request.responseBody = requestMatchFromInstrumentation.responseBody
		// In case the `WebRequest` API request didn't record a request body,
		// but the `XMLHttpRequest` / `fetch` monkey-patch did, use that request body.
		// This is e.g. the case for PATCH requests.
		// Otherwise at least format the request body.
		if (!request.requestBody && requestMatchFromInstrumentation.requestBody) {
			request.requestBody = requestMatchFromInstrumentation.requestBody
		}
	}

	// The matching `fetch` / `XMLHttpRequest` for which no other `WebRequest` API request
	// responded in closer time proximation doesn't pass the response time difference threshold.
	return request
}

export default enrichRequest
